export interface OrgPayroll {
  name: string;
  code: string;
  frequency: 'Weekly' | 'Fortnightly' | 'Monthly';
  region: string;
  employees: number;
  nextPay: string;
  stp: 'sent' | 'failed' | 'not-sent';
  status: 'active' | 'paused' | 'archived';
}

export interface OrgBusinessRecord {
  orgId?: string;
  name: string;
  badge: string;
  legal: {
    country: string;
    number: string;
    branch: string;
    legalName: string;
    tradingName: string;
    taxSignatory: string;
    eft: string;
    withholding: string;
    regionalTax: boolean;
  };
  stp: {
    bmsId: string;
    reportingParty: string;
    branch2: string;
    ssid: string;
    phase: string;
    lastSubmission: string;
    tone: 'brand' | 'caution' | 'positive';
  };
  payrolls: OrgPayroll[];
}

export interface OrgOverview {
  identity: {
    name: string;
    shortCode: string;
    country: string;
    fyEnd: string;
    hqAddress: string;
  };
  contact: {
    primary: string;
    phone: string;
    supportEmail: string;
    senderEmail: string;
  };
  defaults: {
    timezone: string;
    currency: string;
  };
  callout: {
    icon: string;
    tone: 'brand' | 'positive' | 'caution';
    title: string;
    text: string;
  };
}

export interface OrgLabels {
  number: string;
  eft: string;
  withholding: string;
  reportingTitle: string;
  payrollTitle: string;
  bmsId: string;
  branch2: string;
  ssid: string;
}

export interface OrgData {
  label: string;
  badge: string;
  color: string;
  labels: OrgLabels;
  overview: OrgOverview;
  businessRecords: OrgBusinessRecord[];
}

export interface BankAccount {
  name: string;
  bsbLabel: string;
  bsb: string;
  account: string;
  purpose: string;
  status: 'active' | 'inactive';
}

export interface PayAdviceConfig {
  template: string;
  delivery: string[];
  showYtd: boolean;
  showLeave: boolean;
  footer: string;
}

export interface OrgExtra {
  bankAccounts: BankAccount[];
  payAdvice: PayAdviceConfig;
}

export interface RegionInfo {
  country: string;
  state: string;
  city: string;
  phol: string;
}

export interface Holiday {
  date: string;
  name: string;
}

export const ORG_ORDER: string[] = ['nwh-au', 'nwh-uk', 'nwh-nz', 'nwg-au', 'nwg-nz'];

export const ORG_DATA: Record<string, OrgData> = {
  'nwh-au': {
    label: 'Northwind Hospitality Group AU',
    badge: 'NWH',
    color: '#a02050',
    labels: {
      number: 'ABN/Business number',
      eft: 'PAYG EFT code',
      withholding: 'PAYG witholding (days)',
      reportingTitle: 'STP Reporting',
      payrollTitle: 'Payrolls',
      bmsId: 'BMS ID',
      branch2: 'ATO branch',
      ssid: 'SSID (Sender)',
    },
    overview: {
      identity: {
        name: 'Northwind Hospitality Group',
        shortCode: 'NWG',
        country: 'AU',
        fyEnd: 'JUN-30',
        hqAddress: '12 Example Street, Sample City NSW 2000',
      },
      contact: {
        primary: 'Avery Chen',
        phone: '0400 000 111',
        supportEmail: 'support@northwind.example',
        senderEmail: 'no-reply@northwind.example',
      },
      defaults: { timezone: 'Australia/Sydney', currency: 'AUD' },
      callout: {
        icon: 'person',
        tone: 'brand',
        title: 'Multi-employer is enabled',
        text: 'One employee can hold many Employment Records across CBRs. Secondary ER inherits "no tax-free threshold" automatically.',
      },
    },
    businessRecords: [
      {
        name: 'Northwind Hospitality Group AU #1',
        badge: 'NWH',
        legal: {
          country: 'AU',
          number: '11 222 333 444',
          branch: '001',
          legalName: 'Northwind Hospitality Pty Ltd',
          tradingName: 'Northwind Hospitality',
          taxSignatory: 'Avery Chen',
          eft: '10000000001',
          withholding: '0',
          regionalTax: false,
        },
        stp: {
          bmsId: 'BMS-NWG-AU-001',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '001',
          ssid: '—',
          phase: 'Phase 2 in production',
          lastSubmission: '12th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Aotearoa Team - Weekly', code: 'PR-AU-WK-01', frequency: 'Weekly', region: 'Australia - Melbourne Metro', employees: 75, nextPay: 'Wed 13 Jun 2026', stp: 'not-sent', status: 'active' },
          { name: 'Aotearoa Team - Monthly', code: 'PR-AU-MT-01', frequency: 'Monthly', region: 'Australia - Melbourne Metro', employees: 34, nextPay: 'Wed 13 Jun 2026', stp: 'failed', status: 'active' },
          { name: 'Aotearoa Team - Fortnightly', code: 'PR-AU-FT-01', frequency: 'Fortnightly', region: 'Australia - Melbourne Metro', employees: 45, nextPay: 'Wed 13 Jun 2026', stp: 'not-sent', status: 'active' },
        ],
      },
      {
        name: 'Northwind Hospitality Group AU #2',
        badge: 'NWH',
        legal: {
          country: 'AU',
          number: '22 333 444 555',
          branch: '002',
          legalName: 'Northwind Coastal Pty Ltd',
          tradingName: 'Northwind Coastal',
          taxSignatory: 'Priya Nair',
          eft: '10000000002',
          withholding: '0',
          regionalTax: true,
        },
        stp: {
          bmsId: 'BMS-NWG-AU-002',
          reportingParty: 'Registered agent',
          branch2: '002',
          ssid: 'SSID-44821',
          phase: 'Phase 2 in production',
          lastSubmission: '9th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Coastal Venues - Weekly', code: 'PR-AU-WK-02', frequency: 'Weekly', region: 'Australia - Sydney Metro', employees: 52, nextPay: 'Thu 14 Jun 2026', stp: 'sent', status: 'active' },
          { name: 'Coastal Venues - Monthly', code: 'PR-AU-MT-02', frequency: 'Monthly', region: 'Australia - Sydney Metro', employees: 28, nextPay: 'Mon 30 Jun 2026', stp: 'sent', status: 'active' },
        ],
      },
      {
        name: 'Northwind Hospitality Group AU #3',
        badge: 'NWH',
        legal: {
          country: 'AU',
          number: '33 444 555 666',
          branch: '003',
          legalName: 'Northwind Events Pty Ltd',
          tradingName: 'Northwind Events',
          taxSignatory: 'Marcus Webb',
          eft: '10000000003',
          withholding: '2',
          regionalTax: false,
        },
        stp: {
          bmsId: 'BMS-NWG-AU-003',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '003',
          ssid: '—',
          phase: 'Phase 1 (legacy)',
          lastSubmission: '2nd May 2026',
          tone: 'caution',
        },
        payrolls: [
          { name: 'Events Crew - Fortnightly', code: 'PR-AU-FT-03', frequency: 'Fortnightly', region: 'Australia - Brisbane Metro', employees: 18, nextPay: 'Fri 20 Jun 2026', stp: 'not-sent', status: 'paused' },
        ],
      },
    ],
  },

  'nwh-uk': {
    label: 'Northwind Hospitality Group UK',
    badge: 'NWH',
    color: '#a02050',
    labels: {
      number: 'Company number',
      eft: 'PAYE reference',
      withholding: 'Tax period (week)',
      reportingTitle: 'RTI / HMRC',
      payrollTitle: 'Payrolls',
      bmsId: 'Accounts Office ref',
      branch2: 'HMRC office no.',
      ssid: 'Sender ID',
    },
    overview: {
      identity: {
        name: 'Northwind Hospitality UK',
        shortCode: 'NWH-UK',
        country: 'GB',
        fyEnd: 'APR-05',
        hqAddress: '40 Example Road, London EC2A 4NE',
      },
      contact: {
        primary: 'Imogen Clarke',
        phone: '+44 20 7946 0100',
        supportEmail: 'support.uk@northwind.example',
        senderEmail: 'no-reply.uk@northwind.example',
      },
      defaults: { timezone: 'Europe/London', currency: 'GBP' },
      callout: {
        icon: 'info',
        tone: 'brand',
        title: 'Auto-enrolment is active',
        text: 'Eligible workers are automatically enrolled into the workplace pension. Postponement period is set to 1 month.',
      },
    },
    businessRecords: [
      {
        name: 'Northwind Hospitality UK #1',
        badge: 'NWH',
        legal: {
          country: 'GB',
          number: '08412345',
          branch: '—',
          legalName: 'Northwind Hospitality UK Ltd',
          tradingName: 'Northwind Hospitality',
          taxSignatory: 'Imogen Clarke',
          eft: '120/AB12345',
          withholding: '—',
          regionalTax: false,
        },
        stp: {
          bmsId: '120PA00123456',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '120',
          ssid: 'SND-UK-77',
          phase: 'RTI live · FPS submitted',
          lastSubmission: '8th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'London FOH - Monthly', code: 'PR-UK-MT-01', frequency: 'Monthly', region: 'UK - London', employees: 60, nextPay: 'Fri 28 Jun 2026', stp: 'sent', status: 'active' },
          { name: 'Manchester - Weekly', code: 'PR-UK-WK-01', frequency: 'Weekly', region: 'UK - Manchester', employees: 38, nextPay: 'Fri 14 Jun 2026', stp: 'not-sent', status: 'active' },
        ],
      },
      {
        name: 'Northwind Hospitality UK #2',
        badge: 'NWH',
        legal: {
          country: 'GB',
          number: '09553120',
          branch: '—',
          legalName: 'Northwind Catering UK Ltd',
          tradingName: 'Northwind Catering',
          taxSignatory: 'Daniel Osei',
          eft: '120/CD67890',
          withholding: '—',
          regionalTax: false,
        },
        stp: {
          bmsId: '120PA00987654',
          reportingParty: 'Registered agent',
          branch2: '120',
          ssid: 'SND-UK-81',
          phase: 'RTI live · FPS submitted',
          lastSubmission: '6th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Catering - Monthly', code: 'PR-UK-MT-02', frequency: 'Monthly', region: 'UK - Leeds', employees: 22, nextPay: 'Fri 28 Jun 2026', stp: 'failed', status: 'active' },
        ],
      },
    ],
  },

  'nwh-nz': {
    label: 'Northwind Hospitality Group NZ',
    badge: 'NWH',
    color: '#a02050',
    labels: {
      number: 'NZBN',
      eft: 'IRD number',
      withholding: 'Pay period (days)',
      reportingTitle: 'Payday filing',
      payrollTitle: 'Payrolls',
      bmsId: 'myIR account',
      branch2: 'IR dept',
      ssid: 'Gateway ID',
    },
    overview: {
      identity: {
        name: 'Northwind Hospitality NZ',
        shortCode: 'NWH-NZ',
        country: 'NZ',
        fyEnd: 'MAR-31',
        hqAddress: '88 Example Quay, Auckland 1010',
      },
      contact: {
        primary: 'Hera Wynyard',
        phone: '+64 9 887 0100',
        supportEmail: 'support.nz@northwind.example',
        senderEmail: 'no-reply.nz@northwind.example',
      },
      defaults: { timezone: 'Pacific/Auckland', currency: 'NZD' },
      callout: {
        icon: 'info',
        tone: 'brand',
        title: 'KiwiSaver auto-enrolment on',
        text: 'New employees are auto-enrolled at 3%. Employer contribution defaults to 3% of gross earnings.',
      },
    },
    businessRecords: [
      {
        name: 'Northwind Hospitality NZ #1',
        badge: 'NWH',
        legal: {
          country: 'NZ',
          number: '9429041234567',
          branch: '01',
          legalName: 'Northwind Hospitality NZ Ltd',
          tradingName: 'Northwind Hospitality',
          taxSignatory: 'Hera Wynyard',
          eft: '012-3456-789',
          withholding: '—',
          regionalTax: false,
        },
        stp: {
          bmsId: 'myIR-NZ-3041',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '001',
          ssid: 'GW-NZ-22',
          phase: 'Payday filing live',
          lastSubmission: '10th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Auckland Venues - Weekly', code: 'PR-NZ-WK-01', frequency: 'Weekly', region: 'NZ - Auckland', employees: 41, nextPay: 'Wed 11 Jun 2026', stp: 'sent', status: 'active' },
          { name: 'Wellington - Fortnightly', code: 'PR-NZ-FT-01', frequency: 'Fortnightly', region: 'NZ - Wellington', employees: 19, nextPay: 'Wed 18 Jun 2026', stp: 'not-sent', status: 'active' },
        ],
      },
      {
        name: 'Northwind Hospitality NZ #2',
        badge: 'NWH',
        legal: {
          country: 'NZ',
          number: '9429049876543',
          branch: '02',
          legalName: 'Northwind South Island Ltd',
          tradingName: 'Northwind South',
          taxSignatory: 'Tama Reid',
          eft: '012-9876-543',
          withholding: '—',
          regionalTax: false,
        },
        stp: {
          bmsId: 'myIR-NZ-9087',
          reportingParty: 'Registered agent',
          branch2: '002',
          ssid: 'GW-NZ-29',
          phase: 'Payday filing live',
          lastSubmission: '7th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Christchurch - Monthly', code: 'PR-NZ-MT-02', frequency: 'Monthly', region: 'NZ - Christchurch', employees: 24, nextPay: 'Mon 30 Jun 2026', stp: 'sent', status: 'active' },
        ],
      },
    ],
  },

  'nwg-au': {
    label: 'Northwind Group AU',
    badge: 'NWG',
    color: '#6b3fa0',
    labels: {
      number: 'ABN/Business number',
      eft: 'PAYG EFT code',
      withholding: 'PAYG witholding (days)',
      reportingTitle: 'STP Reporting',
      payrollTitle: 'Payrolls',
      bmsId: 'BMS ID',
      branch2: 'ATO branch',
      ssid: 'SSID (Sender)',
    },
    overview: {
      identity: {
        name: 'Northwind Group',
        shortCode: 'NWG-GRP',
        country: 'AU',
        fyEnd: 'JUN-30',
        hqAddress: '1 Example Tower, Sydney NSW 2000',
      },
      contact: {
        primary: 'Jordan Lee',
        phone: '0400 222 333',
        supportEmail: 'group@northwind.example',
        senderEmail: 'no-reply@northwind.example',
      },
      defaults: { timezone: 'Australia/Sydney', currency: 'AUD' },
      callout: {
        icon: 'info',
        tone: 'brand',
        title: 'Group-level configuration',
        text: 'Settings here apply as defaults to every Business Record across the Northwind Group unless overridden.',
      },
    },
    businessRecords: [
      {
        name: 'Northwind Group AU #1',
        badge: 'NWG',
        legal: {
          country: 'AU',
          number: '55 666 777 888',
          branch: '001',
          legalName: 'Northwind Group Holdings Pty Ltd',
          tradingName: 'Northwind Group',
          taxSignatory: 'Jordan Lee',
          eft: '20000000001',
          withholding: '0',
          regionalTax: false,
        },
        stp: {
          bmsId: 'BMS-NWG-GRP-01',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '001',
          ssid: '—',
          phase: 'Phase 2 in production',
          lastSubmission: '12th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Corporate - Monthly', code: 'PR-GRP-MT-01', frequency: 'Monthly', region: 'Australia - Sydney Metro', employees: 96, nextPay: 'Mon 30 Jun 2026', stp: 'sent', status: 'active' },
          { name: 'Executive - Monthly', code: 'PR-GRP-MT-02', frequency: 'Monthly', region: 'Australia - Sydney Metro', employees: 12, nextPay: 'Mon 30 Jun 2026', stp: 'sent', status: 'active' },
        ],
      },
      {
        name: 'Northwind Group AU #2',
        badge: 'NWG',
        legal: {
          country: 'AU',
          number: '66 777 888 999',
          branch: '002',
          legalName: 'Northwind Shared Services Pty Ltd',
          tradingName: 'Northwind Services',
          taxSignatory: 'Sofia Marin',
          eft: '20000000002',
          withholding: '0',
          regionalTax: false,
        },
        stp: {
          bmsId: 'BMS-NWG-GRP-02',
          reportingParty: 'Registered agent',
          branch2: '002',
          ssid: 'SSID-51002',
          phase: 'Phase 2 in production',
          lastSubmission: '11th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Shared Services - Fortnightly', code: 'PR-GRP-FT-01', frequency: 'Fortnightly', region: 'Australia - Brisbane Metro', employees: 33, nextPay: 'Fri 20 Jun 2026', stp: 'not-sent', status: 'active' },
        ],
      },
    ],
  },

  'nwg-nz': {
    label: 'Northwind Group NZ',
    badge: 'NWG',
    color: '#6b3fa0',
    labels: {
      number: 'NZBN',
      eft: 'IRD number',
      withholding: 'Pay period (days)',
      reportingTitle: 'Payday filing',
      payrollTitle: 'Payrolls',
      bmsId: 'myIR account',
      branch2: 'IR dept',
      ssid: 'Gateway ID',
    },
    overview: {
      identity: {
        name: 'Northwind Group NZ',
        shortCode: 'NWG-NZ',
        country: 'NZ',
        fyEnd: 'MAR-31',
        hqAddress: '200 Example Street, Wellington 6011',
      },
      contact: {
        primary: 'Kauri Bennett',
        phone: '+64 4 887 0200',
        supportEmail: 'group.nz@northwind.example',
        senderEmail: 'no-reply.nz@northwind.example',
      },
      defaults: { timezone: 'Pacific/Auckland', currency: 'NZD' },
      callout: {
        icon: 'info',
        tone: 'brand',
        title: 'Single business record',
        text: 'This entity currently operates one Business Record. Add more as the NZ group expands.',
      },
    },
    businessRecords: [
      {
        name: 'Northwind Group NZ #1',
        badge: 'NWG',
        legal: {
          country: 'NZ',
          number: '9429051112223',
          branch: '01',
          legalName: 'Northwind Group NZ Ltd',
          tradingName: 'Northwind Group',
          taxSignatory: 'Kauri Bennett',
          eft: '034-1122-334',
          withholding: '—',
          regionalTax: false,
        },
        stp: {
          bmsId: 'myIR-NZ-5011',
          reportingParty: 'Self-lodging (Employer)',
          branch2: '001',
          ssid: 'GW-NZ-40',
          phase: 'Payday filing live',
          lastSubmission: '9th May 2026',
          tone: 'brand',
        },
        payrolls: [
          { name: 'Wellington HQ - Monthly', code: 'PR-GNZ-MT-01', frequency: 'Monthly', region: 'NZ - Wellington', employees: 27, nextPay: 'Mon 30 Jun 2026', stp: 'sent', status: 'active' },
        ],
      },
    ],
  },
};

export const ORG_EXTRA: Record<string, OrgExtra> = {
  'nwh-au': {
    bankAccounts: [
      { name: 'Northwind Operating · Net Pay', bsbLabel: 'BSB', bsb: '062-000', account: '•••• 4821', purpose: 'Net pay disbursement', status: 'active' },
      { name: 'Northwind PAYG', bsbLabel: 'BSB', bsb: '062-000', account: '•••• 7733', purpose: 'PAYG withholding', status: 'active' },
      { name: 'Northwind Super Clearing', bsbLabel: 'BSB', bsb: '083-004', account: '•••• 1290', purpose: 'Superannuation', status: 'active' },
    ],
    payAdvice: {
      template: 'Standard (AU)',
      delivery: ['Email', 'Employee portal'],
      showYtd: true,
      showLeave: true,
      footer: 'Questions about your pay? Contact payroll@northwind.example.',
    },
  },
  'nwh-uk': {
    bankAccounts: [
      { name: 'Northwind UK · Net Pay', bsbLabel: 'Sort code', bsb: '20-00-00', account: '•••• 5567', purpose: 'Net pay (BACS)', status: 'active' },
      { name: 'Northwind UK · HMRC', bsbLabel: 'Sort code', bsb: '08-32-10', account: '•••• 8890', purpose: 'PAYE / NIC', status: 'active' },
    ],
    payAdvice: {
      template: 'Standard (UK)',
      delivery: ['Email', 'Employee portal'],
      showYtd: true,
      showLeave: false,
      footer: 'Payslips comply with the Employment Rights Act. Contact uk.payroll@northwind.example.',
    },
  },
  'nwh-nz': {
    bankAccounts: [
      { name: 'Northwind NZ · Net Pay', bsbLabel: 'Bank/Branch', bsb: '01-0001', account: '•••• 0033', purpose: 'Net pay disbursement', status: 'active' },
      { name: 'Northwind NZ · IRD', bsbLabel: 'Bank/Branch', bsb: '03-0049', account: '•••• 6611', purpose: 'PAYE / KiwiSaver', status: 'active' },
    ],
    payAdvice: {
      template: 'Standard (NZ)',
      delivery: ['Email'],
      showYtd: true,
      showLeave: true,
      footer: 'Includes KiwiSaver and leave balances. Contact nz.payroll@northwind.example.',
    },
  },
  'nwg-au': {
    bankAccounts: [
      { name: 'Northwind Group · Net Pay', bsbLabel: 'BSB', bsb: '062-001', account: '•••• 1001', purpose: 'Net pay disbursement', status: 'active' },
      { name: 'Northwind Group · PAYG', bsbLabel: 'BSB', bsb: '062-001', account: '•••• 1002', purpose: 'PAYG withholding', status: 'active' },
      { name: 'Northwind Group · Super', bsbLabel: 'BSB', bsb: '083-010', account: '•••• 1003', purpose: 'Superannuation', status: 'active' },
    ],
    payAdvice: {
      template: 'Group standard (AU)',
      delivery: ['Email', 'Employee portal'],
      showYtd: true,
      showLeave: true,
      footer: 'Northwind Group payroll · group.payroll@northwind.example.',
    },
  },
  'nwg-nz': {
    bankAccounts: [
      { name: 'Northwind Group NZ · Net Pay', bsbLabel: 'Bank/Branch', bsb: '02-0500', account: '•••• 7001', purpose: 'Net pay disbursement', status: 'active' },
    ],
    payAdvice: {
      template: 'Group standard (NZ)',
      delivery: ['Email'],
      showYtd: true,
      showLeave: true,
      footer: 'Northwind Group NZ payroll · nz.group.payroll@northwind.example.',
    },
  },
};

export const REGION_INFO: Record<string, RegionInfo> = {
  'Australia - Melbourne Metro': { country: 'Australia', state: 'VIC', city: 'Melbourne', phol: 'VIC public holidays' },
  'Australia - Sydney Metro': { country: 'Australia', state: 'NSW', city: 'Sydney', phol: 'NSW public holidays' },
  'Australia - Brisbane Metro': { country: 'Australia', state: 'QLD', city: 'Brisbane', phol: 'QLD public holidays' },
  'UK - London': { country: 'United Kingdom', state: 'England', city: 'London', phol: 'England & Wales' },
  'UK - Manchester': { country: 'United Kingdom', state: 'England', city: 'Manchester', phol: 'England & Wales' },
  'UK - Leeds': { country: 'United Kingdom', state: 'England', city: 'Leeds', phol: 'England & Wales' },
  'NZ - Auckland': { country: 'New Zealand', state: 'Auckland', city: 'Auckland', phol: 'Auckland + national' },
  'NZ - Wellington': { country: 'New Zealand', state: 'Wellington', city: 'Wellington', phol: 'Wellington + national' },
  'NZ - Christchurch': { country: 'New Zealand', state: 'Canterbury', city: 'Christchurch', phol: 'Canterbury + national' },
};

export const HOLIDAYS: Record<string, Holiday[]> = {
  'Australia': [
    { date: 'Mon 8 Jun 2026', name: "King's Birthday" },
    { date: 'Tue 3 Nov 2026', name: 'Melbourne Cup (VIC)' },
    { date: 'Fri 25 Dec 2026', name: 'Christmas Day' },
    { date: 'Mon 28 Dec 2026', name: 'Boxing Day (obs.)' },
  ],
  'United Kingdom': [
    { date: 'Mon 25 May 2026', name: 'Spring Bank Holiday' },
    { date: 'Mon 31 Aug 2026', name: 'Summer Bank Holiday' },
    { date: 'Fri 25 Dec 2026', name: 'Christmas Day' },
    { date: 'Mon 28 Dec 2026', name: 'Boxing Day (subst.)' },
  ],
  'New Zealand': [
    { date: 'Mon 1 Jun 2026', name: "King's Birthday" },
    { date: 'Fri 23 Oct 2026', name: 'Labour Day' },
    { date: 'Fri 25 Dec 2026', name: 'Christmas Day' },
    { date: "Tue 5 Jan 2027", name: "Day after New Year's (obs.)" },
  ],
};

export const getRegionInfo = (region: string): RegionInfo =>
  REGION_INFO[region] ?? { country: '—', state: '—', city: region, phol: 'National' };
